import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { useAuth } from "react-oidc-context";
import {
  Ambulance,
  BookOpen,
  BriefcaseMedical,
  Code2,
  ScrollText,
  Webhook,
  ClipboardList,
  Hash,
  HeartPulse,
  Home,
  Hospital,
  Loader2,
  LogOut,
  Radio,
  User,
  Users,
} from "lucide-react";
import { ROLE_LABELS } from "@ambulance/shared";
import { useMe } from "../hooks/useMe";

/** Signed-in area: HMG header + role-aware side navigation (ported from the legacy (app) layout). */
export function AppShell() {
  const auth = useAuth();
  const { me, isLoading } = useMe();
  const { pathname } = useLocation();

  if (isLoading || (auth.isAuthenticated && !me)) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--hmg-red)]" />
      </div>
    );
  }
  if (!auth.isAuthenticated) {
    void auth.signinRedirect({ state: { returnTo: pathname } });
    return null;
  }
  if (!me) return null;

  const isAdmin = me.role === "super_admin";
  const isParamedic = me.role === "paramedic";
  const navItems = isParamedic
    ? [
        { href: "/checklist", label: "New Checklist", icon: ClipboardList },
        { href: "/bag-checklist", label: "Bag Checklist", icon: BriefcaseMedical },
        { href: "/unit-login", label: "Unit Login", icon: Radio },
        { href: "/profile", label: "My Profile", icon: User },
      ]
    : [
        { href: "/Checklists-Overview", label: "Checklists", icon: ClipboardList },
        { href: "/bag-checklists", label: "Bag Checklists", icon: BriefcaseMedical },
        { href: "/units", label: "Units", icon: Radio },
        ...(isAdmin
          ? [
              { href: "/pcr-reports", label: "PCR Archive", icon: HeartPulse },
              { href: "/hospitals", label: "Hospitals", icon: Hospital },
              { href: "/ambulances", label: "Ambulances", icon: Ambulance },
              { href: "/unit-codes", label: "Unit Codes", icon: Hash },
              { href: "/users", label: "Users", icon: Users },
              { href: "/api-clients", label: "API Clients", icon: Code2 },
              { href: "/webhooks", label: "Webhooks", icon: Webhook },
              { href: "/audit-log", label: "Audit Log", icon: ScrollText },
              { href: "/developers", label: "Developers", icon: BookOpen },
            ]
          : []),
        { href: "/profile", label: "My Profile", icon: User },
      ];

  return (
    <div className="flex-1 flex flex-col min-h-screen">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 no-print">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2">
          <Link to="/Checklists-Overview" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src="/hmg-logo.png" alt="HMG" width={180} height={48} className="h-8 sm:h-10 w-auto flex-shrink-0" />
            <div className="hidden sm:block border-l border-slate-300 pl-3 min-w-0">
              <div className="text-xs uppercase tracking-wider text-slate-500">EMS</div>
              <div className="text-sm font-semibold text-slate-900 truncate">Ambulance Checklist</div>
            </div>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 p-2 sm:px-3 sm:py-1.5 rounded inline-flex items-center gap-1.5" title="Home">
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <span className="text-sm text-slate-700 hidden lg:inline">
              {me.name ?? me.email}
              <span className="ml-2 inline-block text-xs uppercase tracking-wide text-white bg-[var(--hmg-red)] px-2 py-0.5 rounded">
                {ROLE_LABELS[me.role]}
              </span>
            </span>
            <button
              type="button"
              onClick={() => void auth.signoutRedirect()}
              className="text-sm text-slate-600 hover:text-red-600 p-2 sm:px-3 sm:py-1.5 rounded inline-flex items-center gap-1.5"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto w-full px-3 sm:px-4 py-4 sm:py-6 flex flex-col md:flex-row gap-4 md:gap-6 flex-1">
        <nav className="md:w-56 flex md:flex-col gap-1 flex-wrap no-print overflow-x-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = pathname === item.href || pathname.startsWith(item.href + "/");
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-2 transition ${
                  active ? "bg-[var(--hmg-red)] text-white" : "text-slate-700 hover:bg-slate-200"
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <main className="flex-1 min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
